import { Card, CardBody } from "@components/ui/Card"
import { useCutFormat } from "@hooks/useCutFormat"
import { cn } from "@lib/cn"
import { formatDate, formatList } from "@lib/intl"
import {
  CalendarClock,
  ChartNoAxesColumnIncreasing,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Loader2,
  Sparkles,
  type LucideIcon,
} from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { classroomAdaptiveHref } from "@/constants/adaptiveLevels"
import { ALL_CLASSROOMS } from "@/constants/classroomScope"
import type {
  HomeAdaptivePending,
  HomeClosingCut,
  HomePendientes as HomePendientesShape,
} from "@/types"

/** Exported so consumers annotate their handler with this union rather
 *  than re-typing it and silently drifting when a row is added. */
export type ItemKey =
  | "in_review"
  | "pending_grading"
  | "in_progress"
  | "awaiting"
  | "grading_pending_review"
  | "adaptive_pending"
  | "closing_cut"

interface Props {
  pendientes: HomePendientesShape
  /** PRD-61 §FR-5.4 — a cut closing within a week with work still waiting. */
  closingCut?: HomeClosingCut | null
  /** PRD-48 — adaptive-level recommendations waiting on the professor. */
  adaptivePending?: HomeAdaptivePending | null
  onItemClick?: (item: ItemKey) => void
}

interface Row {
  key: ItemKey
  count: number
  to: string
  icon: LucideIcon
  iconClass: string
  label: string
  /** A quieter second line — where the row lands, when that is one place. */
  detail?: string
}

export const HomePendientes = memo(function HomePendientes({
  pendientes,
  closingCut,
  adaptivePending,
  onItemClick,
}: Props) {
  const { t } = useTranslation("home")
  const navigate = useNavigate()

  // PRD-59: every row is conditional, including this one. It was the single
  // unconditional entry, which meant `allRows` was never empty, which meant
  // `everythingZero` below was never true and the emerald "todo al día" panel
  // — the one reassuring moment this page was built to deliver — was
  // unreachable. On a clear day the professor read "0 Materiales por aprobar".
  const inReviewRow: Row | null =
    pendientes.materials_in_review > 0
      ? {
          key: "in_review",
          count: pendientes.materials_in_review,
          // `classroom=all` is load-bearing, not decoration: these counts are
          // cross-classroom aggregates, but /materiales resolves an unset
          // `classroom` param from the first classroom — so arriving without
          // it silently scopes the list to ONE classroom and shows "Nada por
          // revisar" whenever the pending materials live somewhere else.
          to: `/materiales?status=revision&classroom=${ALL_CLASSROOMS}`,
          icon: Sparkles,
          iconClass: "text-attention",
          label: t("pendientes.in_review"),
        }
      : null

  // Each row appears only when it has something to show: a strip of zeroes
  // reads as a broken counter, and the row with a zero is the one that looks
  // like the answer.
  const pendingGradingRow: Row | null =
    pendientes.pending_grading > 0
      ? {
          key: "pending_grading",
          count: pendientes.pending_grading,
          // PRD-58 — grades are captured on the material page now, so
          // the row lands on the materials list.
          to: "/materiales",
          icon: ClipboardList,
          iconClass: "text-ink-subtle",
          label: t("pendientes.pending_grading"),
        }
      : null

  // PRD-42 §FR-3.3 — this row used to count scheduled materials too;
  // that bucket died with scheduled generation. What's left is
  // `procesando`, which now lasts minutes instead of days — so a
  // permanent "0 generándose" would read as a broken counter on almost
  // every visit. Hide it when zero, like `awaiting` below.
  const inProgressRow: Row | null =
    pendientes.materials_in_progress > 0
      ? {
          key: "in_progress",
          count: pendientes.materials_in_progress,
          // Same cross-classroom reasoning as the in_review row above.
          to: `/materiales?status=in_progress&classroom=${ALL_CLASSROOMS}`,
          icon: Loader2,
          iconClass: "text-accent",
          label: t("pendientes.in_progress"),
        }
      : null

  // PRD-41 §FR-5.1 — photo-graded submissions the professor still has to
  // confirm. Home can't know WHICH material to review, so the row lands
  // on the sent-materials list, where each card carries its own
  // "Revisar entregas (N)" CTA (§FR-5.2) — one hop, no guessing.
  // `classroom=all` for the same reason as the in_review row above.
  // Like `awaiting`, the row only renders when there is something to do:
  // a permanent "0 entregas por revisar" would read as a broken counter
  // on the (common) day nothing needs review.
  const pendingReviewRow: Row | null =
    pendientes.grading_pending_review > 0
      ? {
          key: "grading_pending_review",
          count: pendientes.grading_pending_review,
          // Straight into the queue when every pending cell belongs to
          // one material — the common case, and the whole point of the
          // row. Otherwise the materials list, sorted so the ones that
          // need the professor come first.
          to: pendientes.grading_pending_review_material_id
            ? `/materiales/${pendientes.grading_pending_review_material_id}/revision`
            : `/materiales?status=enviado&sort=priority&classroom=${ALL_CLASSROOMS}`,
          icon: ClipboardCheck,
          iconClass: "text-accent",
          label: t("pendientes.grading_pending_review"),
        }
      : null

  // PRD-48 — level recommendations are decisions only the professor makes.
  // One classroom: straight into its roster with the adaptive dialog open.
  // Several: /salones; each roster's header chip then says how many are its.
  const adaptiveRow: Row | null =
    adaptivePending && adaptivePending.count > 0
      ? {
          key: "adaptive_pending",
          count: adaptivePending.count,
          to:
            adaptivePending.classroom_count === 1 && adaptivePending.classroom_id
              ? classroomAdaptiveHref(adaptivePending.classroom_id)
              : "/salones",
          icon: ChartNoAxesColumnIncreasing,
          iconClass: "text-attention",
          label: t("pendientes.adaptive_pending", { count: adaptivePending.count }),
          // User content (the classroom's name): never translated.
          detail:
            adaptivePending.classroom_count === 1 && adaptivePending.classroom_name
              ? adaptivePending.classroom_name
              : t("pendientes.adaptive_pending_classrooms", {
                  count: adaptivePending.classroom_count,
                }),
        }
      : null

  const awaitingRow: Row | null =
    pendientes.awaiting > 0
      ? {
          key: "awaiting",
          count: pendientes.awaiting,
          // PRD-58 — same as pending_grading: the material owns its results.
          to: "/materiales",
          icon: Clock,
          iconClass: "text-attention",
          label: t("pendientes.awaiting"),
        }
      : null

  const allRows = [
    inReviewRow,
    inProgressRow,
    pendingReviewRow,
    adaptiveRow,
    pendingGradingRow,
    awaitingRow,
  ].filter((r): r is Row => r !== null)
  // Every row is now conditional on being non-zero, so an empty list IS
  // "nothing pending" — which is what the celebratory empty state means.
  const everythingZero = allRows.length === 0 && !closingCut

  return (
    <Card>
      <CardBody className="space-y-3">
        <h2 className="text-ink text-sm font-semibold">{t("pendientes.title")}</h2>
        {everythingZero ? (
          <div className="rounded-control border-success bg-success-soft text-success-ink flex items-center gap-3 border p-3 text-sm">
            <Sparkles className="h-4 w-4" />
            <div>
              <p className="font-medium">{t("pendientes.empty.title")}</p>
              <p className="text-success-ink text-xs">{t("pendientes.empty.message")}</p>
            </div>
          </div>
        ) : (
          <ul className="divide-hairline divide-y">
            {closingCut ? (
              <ClosingCutRow
                closingCut={closingCut}
                onClick={() => {
                  onItemClick?.("closing_cut")
                  navigate(closingCutDestination(closingCut))
                }}
              />
            ) : null}
            {allRows.map((row) => {
              const Icon = row.icon
              const animate = row.key === "in_progress" && row.count > 0
              return (
                <li key={row.key}>
                  <button
                    type="button"
                    onClick={() => {
                      onItemClick?.(row.key)
                      navigate(row.to)
                    }}
                    className={cn(
                      "rounded-control hover:bg-inset focus-visible:ring-accent flex w-full items-center gap-3 p-2 text-left transition-colors focus:outline-none focus-visible:ring-2",
                    )}
                  >
                    <Icon className={cn("h-4 w-4", row.iconClass, animate && "animate-spin")} />
                    <span
                      className={cn(
                        "w-8 text-right text-base font-semibold tabular-nums",
                        "text-ink",
                      )}
                    >
                      {row.count}
                    </span>
                    <span className="text-ink-muted min-w-0 flex-1 text-sm">
                      {row.label}
                      {row.detail ? (
                        <span className="text-meta block truncate">{row.detail}</span>
                      ) : null}
                    </span>
                    <ChevronRight className="text-ink-subtle h-4 w-4" />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  )
})

/**
 * PRD-61 §FR-5.4 — the one dated line in this card: a cut closes within a
 * week and work in it still waits on her. It leads the list because it is the
 * only row with a deadline; the counts below it are undated.
 *
 * One classroom: straight into its roster with Resultados open on this cut —
 * the cut's materials, their graded counts and the review links, the place
 * she acts on exactly this work. Several: /salones, where each card names
 * its running cut and when it closes.
 */
const closingCutDestination = (closingCut: HomeClosingCut): string =>
  closingCut.classroom_count === 1 && closingCut.classroom_id
    ? `/salones/${closingCut.classroom_id}/alumnos?cut=${closingCut.number}`
    : "/salones"

function ClosingCutRow({
  closingCut,
  onClick,
}: {
  closingCut: HomeClosingCut
  onClick: () => void
}) {
  const { t } = useTranslation("home")
  const cutFormat = useCutFormat()
  const cut = cutFormat.label(closingCut)

  const title =
    closingCut.days_left <= 0
      ? t("pendientes.closing_cut.title_today", { cut })
      : closingCut.days_left === 1
        ? t("pendientes.closing_cut.title_tomorrow", { cut })
        : t("pendientes.closing_cut.title_date", {
            cut,
            date: formatDate(closingCut.end_date, t("pendientes.closing_cut.date_format")),
          })

  // Only the non-zero counts, joined the way the locale joins a list
  // ("6 entregas por revisar y 11 sin calificar").
  const items = [
    closingCut.pending_review > 0
      ? t("pendientes.closing_cut.review", { count: closingCut.pending_review })
      : null,
    closingCut.ungraded > 0
      ? t("pendientes.closing_cut.ungraded", { count: closingCut.ungraded })
      : null,
  ].filter((item): item is string => item !== null)

  // Same row vocabulary as the counts below (focus ring included); only the
  // content is two lines, because a date does not fit the number column.
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="rounded-control hover:bg-inset focus-visible:ring-accent flex min-h-11 w-full items-center gap-3 p-2 text-left transition-colors focus:outline-none focus-visible:ring-2"
      >
        <CalendarClock className="text-attention h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="text-ink block text-sm font-medium">{title}</span>
          <span className="text-meta block">
            {closingCut.classroom_count === 1 && closingCut.classroom_name
              ? t("pendientes.closing_cut.detail_named", {
                  items: formatList(items),
                  // User content (the classroom's name): never translated.
                  classroom: closingCut.classroom_name,
                })
              : t("pendientes.closing_cut.detail", {
                  items: formatList(items),
                  count: closingCut.classroom_count,
                })}
          </span>
        </span>
        <ChevronRight className="text-ink-subtle h-4 w-4 shrink-0" aria-hidden />
      </button>
    </li>
  )
}
